import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const BACKEND_URL = 'https://replyfirst-production.up.railway.app';

function Onboarding() {
  const [step, setStep] = useState(1);
  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [notificationPhone, setNotificationPhone] = useState('');
  const [carrier, setCarrier] = useState('');
  const [twilioNumber, setTwilioNumber] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [businessId, setBusinessId] = useState(null);

  const totalSteps = 5;

  const carrierForwardCodes = {
    'Landline':    { on: '*92XXXXXXXXXX', off: '*93' },
    'Cricket':     { on: '**61*+1XXXXXXXXXX**15#', off: '##61#' },
    'AT&T':        { on: '**61*+1XXXXXXXXXX**15#', off: '##61#' },
    'Verizon':     { on: '*71XXXXXXXXXX',           off: '*73'   },
    'T-Mobile':    { on: '**61*+1XXXXXXXXXX**15#', off: '##61#' },
    'Metro':       { on: '**61*+1XXXXXXXXXX**15#', off: '##61#' },
    'Boost':       { on: '**61*+1XXXXXXXXXX**15#', off: '##61#' },
    'US Cellular': { on: '*72XXXXXXXXXX',           off: '*720'  },
  };

  const cancelInstructions = {
    'Landline':    { code: '*93', extra: null },
    'Cricket':     { code: '##61#', extra: null },
    'AT&T':        { code: '##61#', extra: null },
    'Verizon':     { code: '*73', extra: 'After disabling forwarding, you can re-enable Live Voicemail by going to Settings → Apps → Phone → Live Voicemail and turning it back on. Everything returns to normal instantly.' },
    'T-Mobile':    { code: '##61#', extra: null },
    'Metro':       { code: '##61#', extra: null },
    'Boost':       { code: '##61#', extra: null },
    'US Cellular': { code: '*720', extra: null },
  };

  const getForwardingCode = (type) => {
    if (!carrier || !twilioNumber) return null;
    const info = carrierForwardCodes[carrier];
    if (!info) return null;
    if (type === 'off') return info.off;
    const digits = twilioNumber.replace(/\D/g, '').slice(-10);
    return info.on.replace('XXXXXXXXXX', digits);
  };

  useEffect(() => {
    const fetchBusiness = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Look up by user_id — the permanent, always-correct account link —
      // instead of matching email text, which is exactly what kept breaking.
      const { data } = await supabase
        .from('businesses')
        .select('id, business_name, owner_name, business_phone, website_url, notification_phone, twilio_number')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setBusinessId(data.id);
        if (data.twilio_number) setTwilioNumber(data.twilio_number);
        if (data.business_name) setBusinessName(data.business_name);
        if (data.owner_name) setOwnerName(data.owner_name);
        if (data.business_phone) setBusinessPhone(data.business_phone);
        if (data.website_url) setWebsiteUrl(data.website_url);
        if (data.notification_phone) setNotificationPhone(data.notification_phone);
      }
    };
    fetchBusiness();
  }, []);

  const formatPhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return phone;
  };

  const saveToSupabase = async () => {
    setSaving(true);
    setError('');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('Not logged in. Please refresh and try again.');
      setSaving(false);
      return false;
    }

    const payload = {
      owner_email: user.email,
      business_name: businessName.trim(),
      owner_name: ownerName.trim(),
      business_phone: formatPhone(businessPhone),
      website_url: websiteUrl.trim() || null,
      notification_phone: formatPhone(notificationPhone),
      is_active: false,
    };

    // Track the resolved id in a local variable, not just state — setState
    // is async, so relying on the `businessId` state value later in this
    // same function could still read the stale (null) value.
    let resolvedBusinessId = businessId;

    let result;
    if (businessId) {
      result = await supabase.from('businesses').update(payload).eq('id', businessId);
    } else {
      // No existing record found — create one, and capture its id right
      // away so the phone-number purchase below actually has something
      // to work with instead of silently skipping.
      result = await supabase.from('businesses').insert(payload).select('id').single();
      if (!result.error && result.data) {
        resolvedBusinessId = result.data.id;
        setBusinessId(result.data.id);
      }
    }

    if (result.error) {
      setError('Unable to save your information. Please try again.');
      setSaving(false);
      return false;
    }

    if (!twilioNumber && resolvedBusinessId) {
      try {
        const res = await fetch(`${BACKEND_URL}/purchase-number`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ businessId: resolvedBusinessId }),
        });
        const data = await res.json();
        if (data.success && data.twilioNumber) {
          setTwilioNumber(data.twilioNumber);
        } else {
          // Surface this instead of silently swallowing it — a failed
          // number purchase should be visible, not discovered two steps
          // later with no explanation.
          console.error('purchase-number did not return a number:', data);
          setError('We had trouble assigning your ReplyFirst number. Please contact Cole at (614) 795-4971.');
        }
      } catch (err) {
        console.error('purchase-number request failed:', err.message);
        setError('We had trouble assigning your ReplyFirst number. Please contact Cole at (614) 795-4971.');
      }
    }

    setSaving(false);
    return true;
  };

  const handleNext = async () => {
    setError('');

    if (step === 2) {
      if (!businessName.trim()) { setError('Please enter your business name.'); return; }
      if (!ownerName.trim()) { setError('Please enter your name.'); return; }
      if (!businessPhone.trim()) { setError('Please enter your business phone number.'); return; }
    }

    if (step === 3) {
      if (!notificationPhone.trim()) { setError('Please enter a notification phone number.'); return; }
      const saved = await saveToSupabase();
      if (!saved) return;
    }

    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setError('');
    setStep(prev => Math.max(prev - 1, 1));
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #DDE3F0', fontSize: '14px', outline: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0C1A2E',
    boxSizing: 'border-box', backgroundColor: '#fff',
  };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#0C1A2E', marginBottom: '6px' };

  const renderStepIndicator = () => (
    <div style={{ display: 'flex', gap: '6px', marginBottom: '28px' }}>
      {Array.from({ length: totalSteps }).map((_, i) => (
        <div key={i} style={{ flex: 1, height: '4px', borderRadius: '999px', backgroundColor: i + 1 === step ? '#1A56DB' : i + 1 < step ? '#93C5FD' : '#DDE3F0', transition: 'background-color 0.15s' }} />
      ))}
    </div>
  );

  const renderWelcome = () => (
    <div>
      <div style={{ fontSize: '44px', marginBottom: '16px' }}>👋</div>
      <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '24px', fontWeight: 800, color: '#0C1A2E' }}>
        Welcome to ReplyFirst
      </h2>
      <p style={{ marginTop: '10px', fontSize: '14px', color: '#5A6B84', lineHeight: 1.7, marginBottom: '20px' }}>
        You're a few minutes away from never losing a customer to a missed call again. Let's get you set up.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
        {[
          { icon: '🏢', label: 'Step 1', text: 'Tell us about your business' },
          { icon: '📱', label: 'Step 2', text: 'Set where lead alerts go' },
          { icon: '📞', label: 'Step 3', text: 'Turn on call forwarding — 60 seconds' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', backgroundColor: '#F4F7FF', borderRadius: '10px', border: '1px solid #DDE3F0' }}>
            <span style={{ fontSize: '22px' }}>{item.icon}</span>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#9AABBE', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.label}</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#0C1A2E' }}>{item.text}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: '14px 16px', backgroundColor: '#EEF3FF', borderRadius: '10px', border: '1px solid #C3D2F7' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: '#1A56DB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
          Need help? We're here.
        </div>
        <div style={{ fontSize: '13px', color: '#3D4F66', lineHeight: 1.7 }}>
          📞 Call or text Cole: <strong>(614) 795-4971</strong><br />
          ✉️ Email: <strong>colebartimus@getreplyfirst.com</strong>
        </div>
      </div>
    </div>
  );

  const renderBusinessInfo = () => (
    <div>
      <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, color: '#0C1A2E' }}>
        Your business details
      </h2>
      <p style={{ marginTop: '8px', fontSize: '14px', color: '#5A6B84', marginBottom: '20px' }}>
        We use this to personalize your AI assistant. If you have a website, we'll automatically learn your services and hours from it.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={labelStyle}>Business name <span style={{ color: '#F97316' }}>*</span></label>
          <input type="text" value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="e.g. Mike's Auto Shop" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Your name <span style={{ color: '#F97316' }}>*</span></label>
          <input type="text" value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="e.g. Mike Johnson" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Business phone number <span style={{ color: '#F97316' }}>*</span></label>
          <input type="tel" value={businessPhone} onChange={e => setBusinessPhone(e.target.value)} placeholder="e.g. (704) 555-1234" style={inputStyle} />
          <p style={{ marginTop: '4px', fontSize: '12px', color: '#9AABBE' }}>The number your customers currently call</p>
        </div>
        <div>
          <label style={labelStyle}>Business website <span style={{ color: '#9AABBE', fontWeight: 400 }}>(optional but recommended)</span></label>
          <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://www.yourbusiness.com" style={inputStyle} />
          <p style={{ marginTop: '4px', fontSize: '12px', color: '#9AABBE' }}>Our AI will automatically learn your services, hours, and location from your website.</p>
        </div>
      </div>
    </div>
  );

  const renderNotificationSetup = () => (
    <div>
      <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, color: '#0C1A2E' }}>
        Where should we send leads?
      </h2>
      <p style={{ marginTop: '8px', fontSize: '14px', color: '#5A6B84', marginBottom: '20px' }}>
        Every time a customer texts back after a missed call, you'll get a summary here instantly.
      </p>
      <div>
        <label style={labelStyle}>Your cell phone number <span style={{ color: '#F97316' }}>*</span></label>
        <input type="tel" value={notificationPhone} onChange={e => setNotificationPhone(e.target.value)} placeholder="e.g. (704) 555-9876" style={inputStyle} />
        <p style={{ marginTop: '6px', fontSize: '12px', color: '#9AABBE' }}>You can change this anytime in Settings.</p>
      </div>
      <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#0C1A2E', borderRadius: '12px' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: '#7A8EA8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Example lead summary you'll receive</div>
        <div style={{ fontSize: '13px', color: '#fff', lineHeight: 1.8, fontFamily: 'monospace' }}>
          New lead summary:<br />
          Name: John Smith<br />
          Phone: (704) 555-9876<br />
          Need: Oil change<br />
          Urgency: Medium<br />
          Best callback: Tomorrow after 2pm
        </div>
      </div>
      {saving && (
        <div style={{ marginTop: '14px', padding: '10px 14px', backgroundColor: '#EEF3FF', borderRadius: '8px', fontSize: '13px', color: '#1A56DB' }}>
          ⏳ Setting up your ReplyFirst number — this takes a few seconds…
        </div>
      )}
    </div>
  );

  const renderForwardingInstructions = () => {
    const onCode = getForwardingCode('on');
    const isLandline = carrier === 'Landline';

    return (
      <div>
        <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, color: '#0C1A2E' }}>
          Turn on call forwarding
        </h2>
        <p style={{ marginTop: '8px', fontSize: '14px', color: '#5A6B84', marginBottom: '16px' }}>
          Do this on your <strong style={{ color: '#0C1A2E' }}>business phone</strong> — the main number your customers call.
        </p>

        {twilioNumber && (
          <div style={{ padding: '14px 16px', borderRadius: '10px', border: '1px solid #C3D2F7', backgroundColor: '#EEF3FF', marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#1A56DB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Your ReplyFirst number</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#0C1A2E', fontFamily: "'Fraunces', serif" }}>{twilioNumber}</div>
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Select your business phone type</label>
          <select value={carrier} onChange={e => setCarrier(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select your phone type</option>
            <option value="Cricket">Cricket</option>
            <option value="AT&T">AT&T</option>
            <option value="Verizon">Verizon</option>
            <option value="T-Mobile">T-Mobile</option>
            <option value="Metro">Metro by T-Mobile</option>
            <option value="Boost">Boost Mobile</option>
            <option value="US Cellular">US Cellular</option>
            <option value="Landline">📞 Landline</option>
          </select>
        </div>

        {/* Landline instructions */}
        {isLandline && twilioNumber && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #DDE3F0', backgroundColor: '#F4F7FF' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#5A6B84', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Option 1 — Dial this on your landline
              </div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: '#0C1A2E', fontFamily: 'monospace', letterSpacing: '2px', marginBottom: '10px', wordBreak: 'break-all' }}>
                *92{twilioNumber.replace(/\D/g, '').slice(-10)}
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: '#5A6B84', lineHeight: 1.65 }}>
                Pick up your landline, dial the code above, and press call. You'll hear a confirmation tone. If this doesn't work try Option 2 below.
              </p>
            </div>
            <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #DDE3F0', backgroundColor: '#F4F7FF' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#5A6B84', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Option 2 — Call your phone carrier
              </div>
              <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#5A6B84', lineHeight: 1.65 }}>
                Call your landline provider (AT&T, Spectrum, Lumen, etc.) and say:
              </p>
              <div style={{ padding: '12px 14px', backgroundColor: '#0C1A2E', borderRadius: '8px', fontSize: '13px', color: '#fff', lineHeight: 1.7, fontStyle: 'italic' }}>
                "I need to set up conditional call forwarding — when my line is unanswered after 4 rings, please forward to {twilioNumber}."
              </div>
              <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#9AABBE' }}>
                Takes about 5 minutes and they'll handle everything on their end.
              </p>
            </div>
          </div>
        )}

        {/* iPhone Live Voicemail warning — applies to ANY carrier, not just Verizon.
            This is an Apple-side iOS 17+ feature that can intercept calls before
            any carrier's forwarding code fires. */}
        {!isLandline && (
          <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #FED7AA', backgroundColor: '#FFF7ED', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#92400E', marginBottom: '10px' }}>
              📱 iPhone users — do this first:
            </div>
            <p style={{ fontSize: '13px', color: '#92400E', marginBottom: '10px', lineHeight: 1.65 }}>
              iPhone's Live Voicemail feature (iOS 17+) can intercept calls before they ever reach call forwarding — on any carrier, not just one. Turn it off first — don't worry, your regular voicemail still works exactly the same.
            </p>
            <div style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.8 }}>
              1. Open <strong>Settings</strong><br />
              2. Tap <strong>Apps</strong><br />
              3. Tap <strong>Phone</strong><br />
              4. Tap <strong>Live Voicemail</strong><br />
              5. Turn <strong>Live Voicemail off</strong>
            </div>
            <p style={{ fontSize: '12px', color: '#B45309', marginTop: '10px', marginBottom: 0 }}>
              ✅ Your regular voicemail is not affected — customers can still leave voicemails as normal.
            </p>
          </div>
        )}

        {/* Android call-screening caution — Android doesn't have one universal
            feature like Live Voicemail, so this stays general rather than
            naming specific steps that may not apply to every device. */}
        {!isLandline && (
          <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #C3D2F7', backgroundColor: '#EEF3FF', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1A3A7A', marginBottom: '10px' }}>
              🤖 Android users — check this too:
            </div>
            <p style={{ fontSize: '13px', color: '#1A3A7A', lineHeight: 1.65, marginBottom: 0 }}>
              If you have a Pixel phone or any call-screening feature turned on (like Call Screen or a spam-blocking app), turn it off before setting up forwarding — it can prevent calls from ever reaching the forwarding step, the same way Live Voicemail does on iPhone.
            </p>
          </div>
        )}

        {/* Standard carrier forwarding code */}
        {!isLandline && carrier && onCode && (
          <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #DDE3F0', backgroundColor: '#F4F7FF' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#5A6B84', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
              Then dial this on your business phone
            </div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#0C1A2E', fontFamily: 'monospace', letterSpacing: '2px', marginBottom: '14px', wordBreak: 'break-all' }}>{onCode}</div>
            <div style={{ padding: '10px 12px', borderRadius: '8px', backgroundColor: '#FFF7ED', border: '1px solid #FED7AA', marginBottom: '12px' }}>
              <p style={{ margin: 0, fontSize: '12px', color: '#92400E', fontWeight: 600 }}>⚠️ Do this on your business phone — NOT your personal cell</p>
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: '#5A6B84', lineHeight: 1.65 }}>
              Open your dialer, type the code above exactly as shown, and press call. You'll hear a confirmation — then you're all set.
            </p>
          </div>
        )}

        {!twilioNumber && (
          <div style={{ marginTop: '16px', padding: '14px', borderRadius: '10px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}>
            <p style={{ margin: 0, fontSize: '13px', color: '#B91C1C' }}>
              Your ReplyFirst number hasn't been assigned yet. Please contact Cole at (614) 795-4971 and we'll get it sorted right away.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderConfirmation = () => {
    const offCode = carrier ? cancelInstructions[carrier]?.code : null;
    const extraNote = carrier ? cancelInstructions[carrier]?.extra : null;
    const isVerizon = carrier === 'Verizon';
    const isLandline = carrier === 'Landline';

    return (
      <div>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎉</div>
          <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '26px', fontWeight: 800, color: '#0C1A2E' }}>You're live!</h2>
          <p style={{ marginTop: '10px', fontSize: '15px', color: '#5A6B84', lineHeight: 1.7, maxWidth: '380px', margin: '10px auto 0' }}>
            ReplyFirst is now active. Every missed call will be texted back instantly and you'll get a lead summary on your phone.
          </p>
        </div>

        {twilioNumber && (
          <div style={{ padding: '14px 20px', borderRadius: '10px', border: '1px solid #C3D2F7', backgroundColor: '#EEF3FF', textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#1A56DB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Your ReplyFirst number</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#0C1A2E', fontFamily: "'Fraunces', serif" }}>{twilioNumber}</div>
          </div>
        )}

        <div style={{ padding: '14px 16px', borderRadius: '10px', border: '1px solid #DDE3F0', backgroundColor: '#F4F7FF', fontSize: '13px', color: '#5A6B84', lineHeight: 1.8, marginBottom: '16px' }}>
          ✅ Missed calls get texted back in under 15 seconds<br />
          ✅ Lead summaries sent straight to your phone<br />
          ✅ Full conversation log in your dashboard<br />
          <br />
          You can update your info anytime in <strong style={{ color: '#0C1A2E' }}>Settings</strong>.
        </div>

        <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #FED7AA', backgroundColor: '#FFF7ED' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#92400E', marginBottom: '8px' }}>
            If you ever wish to stop using ReplyFirst:
          </div>
          <p style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.65, marginBottom: '12px' }}>
            Simply dial the code below on your business phone to disable call forwarding. Everything goes back to normal instantly — no call to us needed.
          </p>

          {offCode ? (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#B45309', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                Disable forwarding ({carrier}):
              </div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: '#92400E', fontFamily: 'monospace', letterSpacing: '2px', marginBottom: '8px' }}>
                {offCode}
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#92400E', lineHeight: 1.8 }}>
              <strong>Landline:</strong> *93<br />
              <strong>Cricket / AT&T / T-Mobile / Metro / Boost:</strong> ##61#<br />
              <strong>Verizon:</strong> *73<br />
              <strong>US Cellular:</strong> *720
            </div>
          )}

          {isLandline && (
            <p style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.65, marginBottom: '12px' }}>
              If *93 doesn't work, simply call your landline carrier and ask them to remove the call forwarding they set up. Takes about 2 minutes.
            </p>
          )}

          {extraNote && (
            <p style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.65, marginBottom: '12px' }}>
              {extraNote}
            </p>
          )}

          {!extraNote && !isLandline && (
            <p style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.65, marginBottom: '12px' }}>
              Your phone goes back to its normal setup immediately after dialing this code.
            </p>
          )}

          {isVerizon && (
            <p style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.65, marginBottom: '12px' }}>
              After disabling forwarding, you can re-enable Live Voicemail by going to Settings → Apps → Phone → Live Voicemail and turning it back on. Everything returns to normal instantly.
            </p>
          )}

          <p style={{ margin: 0, fontSize: '12px', color: '#B45309' }}>
            Have questions? Call or text Cole at <strong>(614) 795-4971</strong> — we're always happy to help.
          </p>
        </div>
      </div>
    );
  };

  const renderStep = () => {
    switch (step) {
      case 1: return renderWelcome();
      case 2: return renderBusinessInfo();
      case 3: return renderNotificationSetup();
      case 4: return renderForwardingInstructions();
      case 5: default: return renderConfirmation();
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F4F7FF', padding: '32px 16px', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: '560px', backgroundColor: '#fff', borderRadius: '16px', padding: '28px', boxShadow: '0 8px 40px rgba(26,86,219,0.10)', border: '1px solid #DDE3F0' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <div style={{ width: '24px', height: '24px', background: '#1A56DB', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M2 2h12v10H9.5L6 14v-2H2V2z" fill="#fff"/></svg>
              </div>
              <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: '16px', color: '#0C1A2E' }}>ReplyFirst</span>
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: '#9AABBE' }}>Step {step} of {totalSteps}</p>
          </div>
        </header>

        {renderStepIndicator()}

        {error && (
          <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', fontSize: '13px' }}>
            {error}
          </div>
        )}

        <main>{renderStep()}</main>

        <footer style={{ marginTop: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button type="button" onClick={handleBack} disabled={step === 1}
            style={{ padding: '10px 20px', borderRadius: '999px', border: '1px solid #DDE3F0', backgroundColor: step === 1 ? '#F4F7FF' : '#fff', color: step === 1 ? '#9AABBE' : '#3D4F66', fontSize: '14px', fontWeight: 600, cursor: step === 1 ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Back
          </button>
          <button type="button" onClick={step === totalSteps ? undefined : handleNext} disabled={saving || step === totalSteps}
            style={{ padding: '10px 24px', borderRadius: '999px', border: 'none', backgroundColor: step === totalSteps ? '#DDE3F0' : '#F97316', color: step === totalSteps ? '#9AABBE' : '#fff', fontSize: '14px', fontWeight: 700, cursor: step === totalSteps || saving ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {saving ? 'Setting up…' : step === totalSteps ? 'Done ✓' : step === 3 ? 'Save & Continue →' : 'Next →'}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default Onboarding;